import type { LibraryFile, LibraryModel } from '@/types/library';

async function assertOk(res: Response): Promise<Response> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error ? `: ${body.error}` : '';
    } catch {
      // ignore — not all error responses are JSON
    }
    throw new Error(`Request failed (${res.status})${detail}`);
  }
  return res;
}

export async function fetchLibrary(): Promise<LibraryModel[]> {
  const res = await fetch('/api/models');
  await assertOk(res);
  return res.json();
}

export interface SaveToLibraryInput {
  modelName: string;
  sizeLabel: string;
  sourceFormat: string;
  lengthMm: number;
  barrelDiameterMm: number;
  handleDiameterMm: number;
  knobDiameterMm: number;
  profilePoints: number;
  autoTrimConfidence: number;
  files: File[];
}

export async function saveToLibrary(input: SaveToLibraryInput): Promise<{ modelId: string; sizeId: string }> {
  const form = new FormData();
  form.set('modelName', input.modelName);
  form.set('sizeLabel', input.sizeLabel);
  form.set('sourceFormat', input.sourceFormat);
  form.set('lengthMm', String(input.lengthMm));
  form.set('barrelDiameterMm', String(input.barrelDiameterMm));
  form.set('handleDiameterMm', String(input.handleDiameterMm));
  form.set('knobDiameterMm', String(input.knobDiameterMm));
  form.set('profilePoints', String(input.profilePoints));
  form.set('autoTrimConfidence', String(input.autoTrimConfidence));
  for (const file of input.files) {
    form.append('files', file, file.name);
  }

  const res = await fetch('/api/models', { method: 'POST', body: form });
  await assertOk(res);
  return res.json();
}

export async function fetchSizeFiles(modelId: string, sizeId: string): Promise<LibraryFile[]> {
  const res = await fetch(`/api/models/${modelId}/sizes/${sizeId}/files`);
  await assertOk(res);
  return res.json();
}

export async function deleteSize(modelId: string, sizeId: string): Promise<void> {
  const res = await fetch(`/api/models/${modelId}/sizes/${sizeId}/files`, { method: 'DELETE' });
  await assertOk(res);
}

export function fileDownloadUrl(fileId: string): string {
  return `/api/files/${fileId}`;
}

/** Downloads a stored file's actual content (used to reload it back into the Studio). */
export async function fetchFileContent(fileId: string): Promise<string> {
  const res = await fetch(fileDownloadUrl(fileId));
  await assertOk(res);
  return res.text();
}
